/** @typedef {"pending" | "absent" | "extension_only" | "connected" | "device_ready"} PhilipsPdcConnectionStatus */

/**
 * @typedef {Object} PhilipsPdcState
 * @property {PhilipsPdcConnectionStatus} status
 * @property {string | null} deviceTypeName
 * @property {number} deviceCount
 */

/** @type {PhilipsPdcState} */
let state = {
  status: "pending",
  deviceTypeName: null,
  deviceCount: 0,
};

/** @type {Set<(state: PhilipsPdcState) => void>} */
const listeners = new Set();

/** @returns {PhilipsPdcState} */
export function getPhilipsPdcState() {
  return state;
}

/** @returns {PhilipsPdcConnectionStatus} */
export function getPhilipsPdcConnectionStatus() {
  return state.status;
}

/** @param {Partial<PhilipsPdcState>} patch */
export function updatePhilipsPdcState(patch) {
  const next = { ...state, ...patch };
  if (
    next.status === state.status &&
    next.deviceTypeName === state.deviceTypeName &&
    next.deviceCount === state.deviceCount
  ) {
    return;
  }
  state = next;
  listeners.forEach((listener) => listener(state));
}

/** @param {PhilipsPdcConnectionStatus} status */
export function setPhilipsPdcConnectionStatus(status) {
  updatePhilipsPdcState({ status });
}

/** @param {(state: PhilipsPdcState) => void} listener */
export function subscribePhilipsPdcState(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** @param {(status: PhilipsPdcConnectionStatus) => void} listener */
export function subscribePhilipsPdcConnectionStatus(listener) {
  const wrapped = (next) => listener(next.status);
  listeners.add(wrapped);
  return () => listeners.delete(wrapped);
}

/** @returns {boolean} */
export function isPhilipsPdcExtensionAvailable() {
  if (typeof window === "undefined") return false;
  return Boolean(window.PDC);
}

/** @param {number} deviceCount @param {string | null} [deviceTypeName] */
export function syncPhilipsPdcDevicePresence(deviceCount, deviceTypeName = null) {
  const pipelineOk =
    state.status === "connected" || state.status === "device_ready";
  if (!pipelineOk) return;

  if (deviceCount > 0) {
    updatePhilipsPdcState({
      status: "device_ready",
      deviceCount,
      deviceTypeName: deviceTypeName ?? state.deviceTypeName,
    });
    return;
  }

  updatePhilipsPdcState({
    status: "connected",
    deviceCount: 0,
    deviceTypeName: null,
  });
}
